/* =========================================================
   COASTAL DETAIL - v1
   Desktop only: replaces the floating tooltip card with a fixed
   block placed BELOW the swatches, inside the panel that
   globo.accordion.js opens.

   It shows the description of the last swatch HOVERED or SELECTED,
   and keeps it until another swatch is hovered (leaving a swatch
   does not reset it). When the panel closes it falls back to the
   selected value. Options can therefore be compared before
   choosing, with no pop-up at all.

   On touch this module does nothing: the full-screen modal from
   globo.tooltip.js stays the right answer on small screens, and
   the CSS hides the block below 769px.

   Standalone module: it never calls into globo.accordion.js and
   only READS Globo's DOM. Removing the line that loads it in
   snippets/coastal-globo.liquid is enough to get the original
   floating tooltip back.

   NOTE: comments are ASCII-only on purpose - the MCP transport
   used to push theme files rejects payloads containing literal
   non-ASCII characters.
   ========================================================= */
(function () {
  var FIELD_CLASS  = 'coastal-field';
  var DETAIL_CLASS = 'coastal-field__detail';
  var OPEN_CLASS   = 'is-open';   // set by globo.accordion.js

  // Swatch whose description is pinned in each field (last hovered
  // or selected). Without it, the MutationObserver scan below fired
  // on any DOM change of the page and reset the block to the
  // selected value a few ms after every hover.
  var shown = new WeakMap();

  function pinnedItem(field) {
    var item = shown.get(field);
    return item && item.isConnected && field.contains(item) ? item : null;
  }

  // Same breakpoints as isClickMode() in globo.tooltip.js and
  // isTapMode() in globo.accordion.js: all three modules must flip
  // between desktop and touch at exactly the same place.
  var NO_HOVER_MQ = window.matchMedia('(hover: none), (pointer: coarse)');
  var NARROW_MQ   = window.matchMedia('(max-width: 768px)');
  function isTapMode() {
    return NO_HOVER_MQ.matches || NARROW_MQ.matches;
  }

  function closestFrom(node, selector) {
    if (!node || !node.closest) return null;
    return node.closest(selector);
  }

  function upscale(url, size) {
    try {
      var u = new URL(url, location.origin);
      u.pathname = u.pathname.replace(/(\.[a-zA-Z]+)$/, '_' + size + 'x$1');
      return u.toString();
    } catch (e) { return url; }
  }

  // Same formatting as fmtPrice() in globo.tooltip.js: the Globo
  // amount is in the shop base currency (CAD), Shopify.currency.rate
  // converts it to the active market currency.
  function fmtAddon(amount) {
    if (amount == null || amount === '' || isNaN(amount) || Number(amount) === 0) return '';
    var rate = (window.Shopify && Shopify.currency && Shopify.currency.rate)
      ? parseFloat(Shopify.currency.rate) : NaN;
    if (isNaN(rate) || rate <= 0) rate = 1;
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active)
      ? Shopify.currency.active : 'CAD';
    var num;
    try {
      num = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2, maximumFractionDigits: 2
      }).format(Number(amount) * rate);
    } catch (e) { num = (Number(amount) * rate).toFixed(2); }
    return 'Price: + ' + currency + ' ' + num;
  }

  // Everything is read from the tooltip Globo already renders for
  // each swatch: we never modify it, we copy it. It is hidden on
  // desktop by the CSS, but its content stays perfectly readable.
  function readItemDetail(item) {
    if (!item) return null;
    var tip     = item.querySelector('.gpo-tooltip');
    var titleEl = tip && tip.querySelector('.gpo-tooltip__title .break-word');
    var helpEl  = tip && tip.querySelector('.gpo-ov-helptext');
    var imgEl   = item.querySelector('img.swatch-image');
    var input   = item.querySelector('input[data-addon-price]');

    var price = input ? fmtAddon(input.getAttribute('data-addon-price')) : '';
    if (!price && tip) {
      var p = tip.querySelector('.coastal-tooltip-price');
      if (p && p.textContent.trim()) price = p.textContent.trim();
    }

    return {
      title: titleEl ? (titleEl.textContent || '').trim() : '',
      text:  helpEl  ? (helpEl.textContent  || '').trim() : '',
      img:   imgEl && imgEl.src ? upscale(imgEl.src, 400) : '',
      price: price
    };
  }

  function ensureDetail(field) {
    var wrapper = field.querySelector(':scope > .gpo-ov-wrapper');
    if (!wrapper) return null;

    var detail = wrapper.querySelector(':scope > .' + DETAIL_CLASS);
    if (detail) return detail;

    detail = document.createElement('div');
    detail.className = DETAIL_CLASS;
    // Announces the description change to screen readers without
    // stealing focus.
    detail.setAttribute('aria-live', 'polite');
    detail.innerHTML =
      '<img class="coastal-field__detail-img" alt="" loading="lazy">' +
      '<div class="coastal-field__detail-body">' +
        '<span class="coastal-field__detail-title"></span>' +
        '<p class="coastal-field__detail-text"></p>' +
        '<span class="coastal-field__detail-price"></span>' +
      '</div>';
    // Last child of the flex wrapper: with flex-basis 100% it always
    // wraps below the swatch row, whatever the number of swatches.
    wrapper.appendChild(detail);
    return detail;
  }

  // item = swatch to show; when null we fall back to the pinned
  // swatch, then the checked one, then the first one. Every write is guarded by an
  // equality test, otherwise the MutationObserver would re-trigger
  // itself forever.
  function renderDetail(field, item) {
    if (isTapMode()) return;
    var detail = ensureDetail(field);
    if (!detail) return;

    if (!item) item = pinnedItem(field);
    if (!item) {
      var checked = field.querySelector('.gpo-ov-item input:checked');
      item = checked ? closestFrom(checked, '.gpo-ov-item') : null;
    }
    if (!item) item = field.querySelector('.gpo-ov-item');

    var d = readItemDetail(item);
    if (!d) return;

    var img   = detail.querySelector('.coastal-field__detail-img');
    var title = detail.querySelector('.coastal-field__detail-title');
    var text  = detail.querySelector('.coastal-field__detail-text');
    var price = detail.querySelector('.coastal-field__detail-price');
    if (!img || !title || !text || !price) return;

    if (d.img && img.getAttribute('src') !== d.img) img.setAttribute('src', d.img);
    if (img.hidden !== !d.img) img.hidden = !d.img;

    if (title.textContent !== d.title) title.textContent = d.title;
    if (text.textContent !== d.text)   text.textContent  = d.text;
    if (text.hidden !== !d.text)       text.hidden       = !d.text;

    if (price.textContent !== d.price) price.textContent = d.price;
    if (price.hidden !== !d.price)     price.hidden      = !d.price;

    // "None" values: globo.tooltip.js hides their tooltip, so there
    // is neither a title nor a description to show.
    var empty = !d.title && !d.text;
    if (detail.hidden !== empty) detail.hidden = empty;
  }

  // ---------- hover ----------
  // globo.accordion.js is loaded before us and already opens the
  // field on this very event; we only fill the block.
  document.addEventListener('pointerover', function (ev) {
    if (isTapMode()) return;
    if (ev.pointerType && ev.pointerType !== 'mouse') return;

    var f = closestFrom(ev.target, '.' + FIELD_CLASS);
    if (!f) return;
    // Only a swatch changes the description: the gaps, the bar and
    // the block itself keep the pinned one.
    var item = closestFrom(ev.target, '.gpo-ov-item');
    if (!item) return;
    shown.set(f, item);
    renderDetail(f, item);
  });

  // ---------- selection ----------
  document.addEventListener('change', function (ev) {
    var f = closestFrom(ev.target, '.' + FIELD_CLASS);
    if (!f) return;
    // One tick, so Globo has finished rewriting the swatch.
    setTimeout(function () {
      shown.delete(f);
      renderDetail(f, null);
    }, 0);
  });

  // ---------- scan + observe ----------
  function scan() {
    if (isTapMode()) return;
    document.querySelectorAll('.' + FIELD_CLASS).forEach(function (f) {
      // Closed panel: drop the pin, so it reopens on the selected value.
      if (!f.classList.contains(OPEN_CLASS)) shown.delete(f);
      renderDetail(f, null);
    });
  }

  var patching = false;
  var pending  = false;

  // Throttled with setTimeout, never requestAnimationFrame: rAF is
  // suspended in a background tab, while Globo and
  // globo.accordion.js build their DOM asynchronously.
  var mo = new MutationObserver(function () {
    if (patching || pending) return;
    pending = true;
    setTimeout(function () {
      pending = false;
      patching = true;
      try { scan(); } finally { patching = false; }
    }, 16);
  });

  function boot() {
    scan();
    // class: catches the panel closing (is-open removed).
    mo.observe(document.body, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['class']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function onMqChange() { scan(); }
  if (NO_HOVER_MQ.addEventListener) NO_HOVER_MQ.addEventListener('change', onMqChange);
  if (NARROW_MQ.addEventListener)   NARROW_MQ.addEventListener('change', onMqChange);
})();
